/* eslint-disable react/jsx-props-no-spreading */
import { Button } from 'antd'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { isAfter } from 'date-fns'
import { useGetConferencesQuery } from '../../api/conference.api'
import { useGetEventUserQuery } from '../../api/eventUser.api'
import { useMyContext } from '../../context/context'
import useConf from '../../hooks/useConf'
import useSettingsV2 from '../../hooks/useSettingsV2'
import useTranslations from '../../hooks/useTranslations'
import Conferences from '../Conferences/Conferences'
import Companies from '../companies/Companies'
import Articles from './Articles'
import Confirmation from './Confirmation'
import Dates from './Dates'
import Dates2 from './Dates2'
import Informations from './Informations'
import Informations2 from './Informations2'
import Participation from './Participation'
import Quotas from './Quotas'
import RepresentedBy from './RepresentedBy'
import Typos from './Typos'
import Categories from './categories'
import ConferencesV2 from './conferencesV2'
import Resume from './resume'
import Informations3 from './Informations3'

const StepsContext = createContext(null)

export const useSteps = () => {
  const context = useContext(StepsContext)
  if (!context) {
    return {}
  }
  return context
}

const StepsContextProvider = () => {
  const [userId, setUserId] = useState()
  const [stepId, setStepId] = useState(-1)
  const [participation, setParticipation] = useState()
  const [hasFinished, setHasFinished] = useState(false)
  const [daysId, setDaysId] = useState([])
  const [articles, setArticles] = useState({})
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  const { myContext, userId: myUserId, isFetchingUser } = useMyContext()
  const { settings, isFetching: settingsFetching } = useSettingsV2()

  // « La navigation est en train de se stabiliser » : vrai tant que le eventUser OU les settings
  // (scopés par typo) se re-fetchent. Choisir une typo invalide EVENT_USER puis recharge les
  // settings, ce qui peut AJOUTER/retirer des étapes (ex. Articles n'existe que pour certaines
  // typos). Tant que c'est vrai, les étapes qui s'auto-sautent (Dates 1 jour, etc.) doivent
  // ATTENDRE : sinon `stepId` (un simple index) dépasse l'emplacement où Articles va être inséré
  // et l'étape est sautée pour toujours. Voir Dates.jsx.
  const settling = useMemo(() => Boolean(isFetchingUser || settingsFetching), [isFetchingUser, settingsFetching])
  const {
    showConferences,
    articlesActive,
    articlesHide,
    showInfo2,
    companiesShow,
    showParticipation,
    COMPANIES_LABEL,
    QUOTAS_SHOW,
    datesHide,
    showConferencesV2,
  } = useConf(participation)

  const { data: user } = useGetEventUserQuery(myContext)
  const { data: user2 } = useGetEventUserQuery({ ...myContext, userId })
  const { data: conferences } = useGetConferencesQuery(myContext)

  const { t } = useTranslations()

  const isMe = useMemo(() => userId && userId === myUserId, [myUserId, userId])

  const isClosed = useMemo(() => {
    const endDate = settings?.FO_MEETING_REGISTER_END

    if (!endDate) {
      return false
    }

    return isAfter(new Date(), new Date(endDate))
  }, [settings])

  useEffect(() => {
    if (userId) {
      setArticles({})
    }
  }, [userId])

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const { FO_MEETING_CONFERENCES_LABEL, FO_MEETING_TYPO_IS_ACTIVE, FO_MEETING_INFO_2_LABEL, FO_MEETING_TYPO_LABEL } = settings

  const nextStep = useCallback(() => {
    setStepId(prev => prev + 1)
  }, [])
  const resetSteps = useCallback((p, u) => {
    setParticipation(p || null)
    setStepId(0)
    setUserId(u || null)
    // if (Array.isArray(event.days) && p === 5) {
    //   if (user?.UserEvent) {
    //     setDaysId(user?.UserEvent?.UserEventDays?.filter(ued => ued.registered)?.map(ued => ued.eventDayId))
    //   }
    // }
  }, [])

  const showInfo = useMemo(() => {
    return settings?.FO_MEETING_INFO_IS_ACTIVE
  }, [settings])

  const steps = useMemo(() => {
    return [
      // { label: settings?.FO_MEETING_CONFERENCES_V2_LABEL || 'Conférences', component: <ConferencesV2 /> },
      ...(participation !== 5 ? [{ label: 'Participation', component: <Participation />, hide: !showParticipation }] : []),
      ...(participation === 2
        ? [
            { label: 'Invitation', component: <RepresentedBy /> },
            ...(showInfo && settings?.FO_MEETING_REPRESENTED_FORM_ID
              ? [{ label: settings?.FO_MEETING_INFO_LABEL || 'Informations', component: <Informations /> }]
              : []),
          ]
        : []),
      ...(participation !== 2
        ? [
            ...(FO_MEETING_TYPO_IS_ACTIVE && participation !== 5
              ? [
                  {
                    label: FO_MEETING_TYPO_LABEL || 'Typologie',
                    component: <Typos />,
                    hide:
                      ((user2?.UserEvent?.typo || user2?.UserEvent?.typoId) && settings?.FO_MEETING_TYPO_NEXT_STEP) ||
                      (user2?.isExhibitor && settings?.FO_MEETING_TYPO_DEFAULT_EXHIBITOR),
                  },
                ]
              : []),
            ...(participation !== 5 ? [{ label: t('EVENT_DATES_TITLE'), component: <Dates />, hide: datesHide }] : []),
            ...(participation !== 5 && articlesActive
              ? [{ label: settings?.FO_MEETING_ARTICLES_LABEL || 'Articles', component: <Articles />, hide: articlesHide }]
              : []),
            ...(showInfo ? [{ label: settings?.FO_MEETING_INFO_LABEL || 'Informations', component: <Informations /> }] : []),
            ...(participation === 5 ? [{ label: t('EVENT_DATES_TITLE'), component: <Dates />, hide: datesHide }] : []),
            ...(participation === 5 && articlesActive
              ? [{ label: settings?.FO_MEETING_ARTICLES_LABEL || 'Articles', component: <Articles />, hide: articlesHide }]
              : []),
            ...(companiesShow ? [{ label: COMPANIES_LABEL || 'Sociétés', component: <Companies /> }] : []),
            ...(showConferences
              ? [{ label: FO_MEETING_CONFERENCES_LABEL || 'Conférences', component: <Conferences userId={userId} nextStep={nextStep} /> }]
              : []),
            ...(showConferencesV2 && participation !== 5
              ? [
                  { label: settings?.FO_MEETING_CATEGORIES_LABEL || 'Catégories', component: <Categories /> },
                  { label: settings?.FO_MEETING_CONFERENCES_V2_LABEL || 'Conférences', component: <ConferencesV2 /> },
                ]
              : []),
            ...(showInfo2 ? [{ label: FO_MEETING_INFO_2_LABEL || 'Formulaire', component: <Informations2 /> }] : []),
            ...(settings?.FO_MEETING_INFO_3_IS_ACTIVE
              ? [{ label: settings?.FO_MEETING_INFO_3_LABEL || 'Formulaire', component: <Informations3 /> }]
              : []),
            ...(QUOTAS_SHOW ? [{ label: settings?.FO_MEETING_QUOTAS_LABEL || 'Quotas', component: <Quotas /> }] : []),
            { label: t('EVENT_DATES_TITLE'), component: <Dates2 />, hide: true },
          ]
        : []),
      { label: settings?.FO_MEETING_CONFIRMATION_LABEL || 'Confirmation', component: <Confirmation /> },
    ]
  }, [
    participation,
    showParticipation,
    showInfo,
    settings,
    FO_MEETING_TYPO_IS_ACTIVE,
    FO_MEETING_TYPO_LABEL,
    user2,
    t,
    datesHide,
    articlesActive,
    articlesHide,
    companiesShow,
    COMPANIES_LABEL,
    showConferences,
    FO_MEETING_CONFERENCES_LABEL,
    userId,
    nextStep,
    showConferencesV2,
    showInfo2,
    FO_MEETING_INFO_2_LABEL,
    QUOTAS_SHOW,
  ])

  const totalSteps = useMemo(() => steps.length - 1, [steps])

  const step = useMemo(() => steps[stepId], [stepId, steps])
  const goToEnd = useCallback(() => setStepId(totalSteps), [totalSteps])
  const isEnd = useMemo(() => stepId === totalSteps, [stepId, totalSteps])

  useEffect(() => {
    if (!settings) return

    if (isClosed) {
      goToEnd()
      setHasFinished(true)
    } else if (stepId === -1 && user) {
      if (user?.UserEvent?.responded || user?.UserEvent?.registered) {
        if (conferences?.length < 1 && settings?.CONFERENCE_MIN_1) {
          setStepId(0)
        } else {
          goToEnd()
          setHasFinished(true)
        }
      } else {
        setStepId(0)
      }
    }
  }, [companiesShow, conferences, goToEnd, isClosed, participation, settings, stepId, user])

  const NextButton = useCallback(
    props => {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 16 }}>
          {hasFinished ? <Button onClick={goToEnd}>{t('CANCEL')}</Button> : <div />}
          <Button type="primary" {...props}>
            {t('VALIDATE')}
          </Button>
        </div>
      )
    },
    [goToEnd, hasFinished, t]
  )

  const CompanionName = useMemo(
    () =>
      participation === 5 && user2 && stepId < totalSteps ? (
        <div style={{ color: 'black', fontSize: 16, marginBottom: 20, fontWeight: 500 }}>
          {t('REGISTERING_FOR', { values: [user2.firstName, user2.lastName], valueDefault: 'Vous inscrivez : $1 $2' })}
        </div>
      ) : null,
    [participation, stepId, totalSteps, user2, t]
  )

  const state = useMemo(
    () => ({
      setUserId,
      nextStep,
      userId,
      isMe,
      setParticipation,
      participation,
      goToEnd,
      resetSteps,
      NextButton,
      CompanionName,
      setDaysId,
      daysId,
      isEnd,
      articles,
      setArticles,
      isClosed,
      screenWidth,
      settling,
    }),
    [
      nextStep,
      userId,
      isMe,
      participation,
      goToEnd,
      resetSteps,
      NextButton,
      CompanionName,
      daysId,
      isEnd,
      articles,
      isClosed,
      screenWidth,
      settling,
    ]
  )

  return (
    <StepsContext.Provider value={state}>
      <div style={{ backgroundColor: '#F5F6F5', padding: 25 }}>
        <div style={{ backgroundColor: 'white', padding: 25, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: screenWidth >= 1000 && 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexDirection: screenWidth < 1000 ? 'column' : 'row',
            }}
          >
            {!isEnd ? (
              <>
                <ul className="steps is-small" style={{ flexGrow: 1 }}>
                  {steps
                    .reduce((p, m) => {
                      const prev = p[p.length - 1]
                      if (prev && m.hide) {
                        return [...p, { ...m, number: prev.number }]
                      }
                      if (prev) {
                        return [...p, { ...m, number: prev.number + 1 }]
                      }
                      return [...p, { ...m, number: m.hide ? 0 : 1 }]
                    }, [])
                    .map(({ label, hide, number }, i) => {
                      if (hide) {
                        return null
                      }

                      return (
                        <li
                          className={`step-item ${stepId > i && 'is-completed is-success'} ${stepId === i && 'is-active'} ${stepId ===
                            steps.length && 'is-completed is-success'}`}
                          key={`step-${i}`}
                        >
                          <div className="step-marker">{number}</div>
                          <div className="step-details">
                            <p className="step-title">
                              {t('STEP')} {number}
                            </p>
                            <p>{label}</p>
                          </div>
                        </li>
                      )
                    })}
                </ul>
                <Resume />
              </>
            ) : (
              <div />
            )}
          </div>
          <div>{step?.component}</div>
        </div>
      </div>
    </StepsContext.Provider>
  )
}

export default StepsContextProvider
